import React from "react";
import { motion } from "framer-motion";
import {
  Compass,
  Users,
  ChartLineUp,
  Megaphone,
  GearSix,
  Sparkle,
  CurrencyDollar,
  UserPlus,
  Crown,
  Bank,
  RocketLaunch,
  Calculator,
  ChartPieSlice,
  GlobeHemisphereWest,
  PresentationChart,
  ArrowsMerge,
  ShieldCheck,
  Lifebuoy,
  Factory,
  TShirt,
  Robot,
  ClipboardText,
  FlowArrow,
} from "@phosphor-icons/react";
import { Reveal } from "../lib/motion";
import { Eyebrow, PrimaryButton } from "../components/ui-kit";

const OFFERINGS = [
  { icon: Compass, title: "Strategy & Direction", accent: "teal", desc: "Business direction, growth planning, and decision frameworks that keep the whole company aligned." },
  { icon: Users, title: "Leadership & Coaching", accent: "gold", desc: "Founder coaching, leadership development, and team effectiveness for the people who build the company." },
  { icon: ChartLineUp, title: "Sales & Revenue", accent: "teal", desc: "Revenue strategy, sales systems, and go-to-market execution that move you beyond founder-led selling." },
  { icon: Megaphone, title: "Marketing & Brand", accent: "gold", desc: "Brand positioning, demand generation, and content strategy that build trust and pipeline." },
  { icon: GearSix, title: "Operations & Systems", accent: "teal", desc: "Business processes, KPIs, and operational excellence — the machinery that lets growth compound." },
  { icon: Sparkle, title: "AI Adoption", accent: "gold", desc: "Leverage AI across functions to improve productivity, decision-making, and customer experience." },
  { icon: CurrencyDollar, title: "Fundraising Readiness", accent: "teal", desc: "Narrative, metrics, and data-room preparation to help you raise with confidence." },
  { icon: UserPlus, title: "Talent & Hiring", accent: "gold", desc: "Hiring your first leaders, structuring teams, and building a culture that scales." },
  { icon: Crown, title: "Fractional Leadership", accent: "teal", desc: "Hands-on interim leadership across strategy, revenue, or operations when you need it most." },
  { icon: Bank, title: "Board & Governance", accent: "gold", desc: "Building an effective board, investor relations, and the governance a growing company needs." },
  { icon: RocketLaunch, title: "Go-to-Market Execution", accent: "teal", desc: "From positioning to launch — a repeatable motion that turns traction into momentum." },
  { icon: Calculator, title: "Financial Planning & Metrics", accent: "gold", desc: "Unit economics, forecasting, and the numbers that guide sustainable, profitable growth." },
];

const EXTENDED = [
  {
    icon: ChartPieSlice,
    title: "CFO & Financial Leadership",
    points: ["Cashflow and working capital", "Budgeting, MIS, and dashboards", "Board and investor reporting"],
  },
  {
    icon: GlobeHemisphereWest,
    title: "Tax & Cross-Border Structuring",
    points: ["Cross-border tax planning", "Transfer pricing", "Entity and holding structures"],
  },
  {
    icon: PresentationChart,
    title: "Capital & Investor Readiness",
    points: ["Financial models and valuation", "Investor decks and materials", "Equity and debt raises"],
  },
  {
    icon: ArrowsMerge,
    title: "Mergers & Acquisitions",
    points: ["Sell-side advisory", "Buy-side and target search", "Structuring and close"],
  },
  {
    icon: ShieldCheck,
    title: "Reporting & Compliance",
    points: ["Group consolidation", "Statutory and audit-ready accounts", "Multi-GAAP reporting"],
  },
  {
    icon: Lifebuoy,
    title: "Interim & Special Situations",
    points: ["Interim finance leadership", "Turnaround support", "Surge capacity for live deals"],
  },
  {
    icon: Factory,
    title: "Manufacturing Strategy & Factory Setup",
    points: ["Site & layout planning", "Capex and sourcing strategy", "Workflow and process design"],
  },
  {
    icon: TShirt,
    title: "Apparel & Consumer Product Development",
    points: ["Design and construction", "Costing and feasibility", "Sampling and sign-off"],
  },
  {
    icon: Robot,
    title: "Factory Digitization & AI-Driven Operations",
    points: ["Production tracking systems", "AI-assisted operations", "Real-time reporting"],
  },
  {
    icon: ClipboardText,
    title: "Production Planning & Quality Systems",
    points: ["Capacity and scheduling", "Quality control frameworks", "Vendor performance management"],
  },
  {
    icon: FlowArrow,
    title: "Operations Excellence & Process Automation",
    points: ["SOPs and workflows", "Order and inventory automation", "Continuous improvement"],
  },
];

// Continuously animated icon (float + wobble), with an extra pop on card hover.
function AnimatedIcon({ Icon, accent, delay }) {
  const gold = accent === "gold";
  return (
    <motion.div
      data-testid="animated-icon"
      className={`grid h-14 w-14 place-items-center rounded-2xl ${
        gold ? "bg-gold/12 text-gold" : "bg-teal/10 text-teal"
      }`}
      animate={{ y: [0, -7, 0], rotate: [0, -6, 6, 0] }}
      transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay }}
      variants={{ hover: { scale: 1.18 } }}
    >
      <Icon size={26} weight="duotone" />
    </motion.div>
  );
}

function OfferingCard({ item, i }) {
  const Icon = item.icon;
  return (
    <Reveal delay={(i % 3) * 0.08}>
      <motion.div
        initial="rest"
        animate="rest"
        whileHover="hover"
        data-testid={`offering-card-${i}`}
        className="group h-full rounded-3xl border border-slate-100 bg-white p-9 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(11,31,58,0.10)]"
      >
        <AnimatedIcon Icon={Icon} accent={item.accent} delay={(i % 6) * 0.25} />
        <h3 className="mt-7 font-heading text-2xl font-semibold text-navy">{item.title}</h3>
        <p className="mt-4 leading-relaxed text-slate-600">{item.desc}</p>
      </motion.div>
    </Reveal>
  );
}

export default function Offerings() {
  return (
    <div data-testid="offerings-page">
      {/* Hero */}
      <section className="bg-cloud pt-40 pb-20 lg:pt-48 lg:pb-24">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Offerings</Eyebrow>
            <h1 className="mt-7 max-w-4xl font-heading text-5xl font-light leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-[4.2rem]">
              What we bring to the{" "}
              <span className="font-semibold text-gold">partnership.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
              A clear set of ways we build alongside founders — from the core capabilities
              every company needs to the extended support that unlocks the next stage.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Offerings — all 12 */}
      <section className="bg-white py-24 lg:py-32" data-testid="offerings-core-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Offerings</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              Core ways we build with you.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {OFFERINGS.map((item, i) => (
              <OfferingCard key={item.title} item={item} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Extended Offerings — placeholder */}
      <section className="bg-cloud py-24 lg:py-32" data-testid="offerings-extended-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Extended Offerings</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              Deeper support when the stage demands it.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {EXTENDED.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={(i % 3) * 0.08}>
                  <motion.div
                    initial="rest"
                    animate="rest"
                    whileHover="hover"
                    data-testid={`extended-card-${i}`}
                    className="group h-full rounded-3xl border border-slate-100 bg-white p-9 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(11,31,58,0.10)]"
                  >
                    <AnimatedIcon Icon={Icon} accent="gold" delay={(i % 6) * 0.25} />
                    <h3 className="mt-7 font-heading text-2xl font-semibold text-navy">{s.title}</h3>
                    <ul className="mt-5 space-y-2.5">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-center gap-3 text-[15px] text-slate-600">
                          <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-24 text-center lg:py-28">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-heading text-4xl font-light tracking-tight text-white sm:text-5xl">
              Let's find the right way to build together.
            </h2>
            <div className="mt-9 flex justify-center">
              <PrimaryButton
                to="/contact"
                testid="offerings-cta"
                className="bg-gold text-navy hover:bg-gold-light"
              >
                Schedule a Conversation
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
